import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dpsTheme } from '../../styles/dpsTheme';
import {
  Notification,
  getNotifications,
  markAsRead,
  markAllAsRead,
  subscribeToNotifications,
} from '../../services/notificationService';

interface NotificationBellProps {
  userId: string;
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "à l'instant";
  if (diffMins < 60) return `il y a ${diffMins} min`;
  if (diffHours < 24) return `il y a ${diffHours}h`;
  if (diffDays === 1) return 'hier';
  if (diffDays < 7) return `il y a ${diffDays} jours`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await getNotifications(15);
      setNotifications(data);
      setUnreadCount(data.filter((n) => !n.read).length);
    } catch {
      // silently fail
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Realtime subscription
  useEffect(() => {
    const unsubscribe = subscribeToNotifications(userId, loadNotifications);
    return unsubscribe;
  }, [userId, loadNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const handleNotificationClick = async (n: Notification) => {
    if (!n.read) {
      await markAsRead(n.id);
      setNotifications((prev) =>
        prev.map((item) => (item.id === n.id ? { ...item, read: true } : item))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    setIsOpen(false);
    if (n.data.diagnostic_id) {
      navigate(`/dps/report/${n.data.diagnostic_id}/actions`);
    }
  };

  const handleMarkAllAsRead = async () => {
    setLoading(true);
    try {
      await markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const hasUnread = unreadCount > 0;

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        title="Notifications"
        style={{
          position: 'relative',
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          border: `1px solid ${isOpen ? dpsTheme.colors.accent.blue.secondary : dpsTheme.colors.border.primary}`,
          background: isOpen ? 'rgba(59, 130, 246, 0.1)' : 'rgba(255, 255, 255, 0.05)',
          color: isOpen ? dpsTheme.colors.accent.blue.secondary : dpsTheme.colors.text.secondary,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = dpsTheme.colors.text.primary;
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.color = dpsTheme.colors.text.secondary;
          }
        }}
      >
        {/* Bell icon */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {/* Unread badge */}
        {hasUnread && (
          <span
            style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              width: unreadCount > 9 ? '18px' : '14px',
              height: '14px',
              borderRadius: '7px',
              background: '#ef4444',
              color: 'white',
              fontSize: '9px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              border: '1.5px solid rgba(15, 23, 42, 0.9)',
              letterSpacing: '-0.3px',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: '340px',
            background: 'rgba(15, 23, 42, 0.97)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${dpsTheme.colors.border.primary}`,
            borderRadius: '12px',
            boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            zIndex: 200,
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px',
              borderBottom: `1px solid ${dpsTheme.colors.border.secondary}`,
            }}
          >
            <span
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: dpsTheme.colors.text.primary,
              }}
            >
              Notifications
              {hasUnread && (
                <span
                  style={{
                    marginLeft: '8px',
                    background: '#ef4444',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: '10px',
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </span>
            {hasUnread && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={loading}
                style={{
                  fontSize: '12px',
                  color: dpsTheme.colors.accent.blue.secondary,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  opacity: loading ? 0.5 : 1,
                  padding: '2px 4px',
                  borderRadius: '4px',
                  transition: 'opacity 0.15s',
                }}
              >
                Tout marquer comme lu
              </button>
            )}
          </div>

          {/* Notification list */}
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: '32px 16px',
                  textAlign: 'center',
                  color: dpsTheme.colors.text.muted,
                  fontSize: '13px',
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  style={{ margin: '0 auto 12px', display: 'block', opacity: 0.4 }}
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                Aucune notification
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px 16px',
                    background: n.read ? 'transparent' : 'rgba(59, 130, 246, 0.06)',
                    border: 'none',
                    borderBottom: `1px solid ${dpsTheme.colors.border.secondary}`,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = n.read
                      ? 'rgba(255,255,255,0.04)'
                      : 'rgba(59, 130, 246, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = n.read
                      ? 'transparent'
                      : 'rgba(59, 130, 246, 0.06)';
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '50%',
                      background: n.read
                        ? 'rgba(148, 163, 184, 0.1)'
                        : 'rgba(59, 130, 246, 0.15)',
                      border: `1px solid ${n.read ? 'rgba(148, 163, 184, 0.15)' : 'rgba(59, 130, 246, 0.3)'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: n.read
                        ? dpsTheme.colors.text.muted
                        : dpsTheme.colors.accent.blue.secondary,
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Titre */}
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: n.read ? 400 : 600,
                        color: n.read
                          ? dpsTheme.colors.text.secondary
                          : dpsTheme.colors.text.primary,
                        marginBottom: '3px',
                        lineHeight: 1.4,
                      }}
                    >
                      {n.title}
                    </div>

                    {/* Référence client + compteur d'actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px', flexWrap: 'wrap' }}>
                      {n.data.client_reference ? (
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: dpsTheme.colors.accent.blue.secondary,
                            background: 'rgba(59, 130, 246, 0.12)',
                            border: '1px solid rgba(59, 130, 246, 0.25)',
                            borderRadius: '4px',
                            padding: '1px 6px',
                            letterSpacing: '0.3px',
                          }}
                        >
                          {n.data.client_reference}
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: dpsTheme.colors.text.muted }}>
                          Client
                        </span>
                      )}
                      {n.data.count != null && (
                        <span style={{ fontSize: '12px', color: dpsTheme.colors.text.muted }}>
                          {n.data.count} action{n.data.count > 1 ? 's' : ''} mise{n.data.count > 1 ? 's' : ''} à jour
                        </span>
                      )}
                    </div>

                    {/* Dernière action */}
                    {n.data.last_action_title && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: dpsTheme.colors.text.muted,
                          opacity: 0.7,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontStyle: 'italic',
                          marginBottom: '3px',
                        }}
                      >
                        « {n.data.last_action_title} »
                      </div>
                    )}

                    {/* Heure */}
                    <div
                      style={{
                        fontSize: '11px',
                        color: dpsTheme.colors.text.muted,
                        opacity: 0.6,
                      }}
                    >
                      {timeAgo(n.updated_at)}
                    </div>
                  </div>

                  {/* Unread dot */}
                  {!n.read && (
                    <div
                      style={{
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        background: dpsTheme.colors.accent.blue.secondary,
                        flexShrink: 0,
                        marginTop: '4px',
                      }}
                    />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
